import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Settings } from 'lucide-react';

const CONSENT_KEY = 'expert-handyman-cookie-consent';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, required for site to function
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      const savedPrefs = JSON.parse(consent);
      setPreferences(savedPrefs);
      // Initialize analytics if consented
      if (savedPrefs.analytics) {
        initializeAnalytics();
      }
    }
  }, []);

  const initializeAnalytics = () => {
    // Initialize Google Analytics if not already loaded
    if (window.gtag) return;
    
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (!GA_ID) return;

    // Load gtag script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, {
      'anonymize_ip': true,
      'cookie_flags': 'SameSite=None;Secure'
    });
  };

  const handleAcceptAll = () => {
    const newPrefs = { necessary: true, analytics: true, marketing: true };
    setPreferences(newPrefs);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newPrefs));
    initializeAnalytics();
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const newPrefs = { necessary: true, analytics: false, marketing: false };
    setPreferences(newPrefs);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newPrefs));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
    if (preferences.analytics) {
      initializeAnalytics();
    }
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[60] p-3 md:p-4 bg-white border-t border-border shadow-lg animate-in slide-in-from-bottom duration-500"
        data-testid="cookie-banner"
      >
        <div className="max-w-7xl mx-auto">
          {!showSettings ? (
            // Simple Banner - compact on mobile
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground flex-1">
                We use cookies to improve your experience.{' '}
                <button 
                  onClick={() => setShowSettings(true)}
                  className="text-primary hover:underline"
                >
                  Manage
                </button>
              </p>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleAcceptNecessary}
                  className="flex-1 sm:flex-none text-xs h-8"
                  data-testid="cookie-reject-btn"
                >
                  Necessary Only
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-none btn-accent text-xs h-8"
                  data-testid="cookie-accept-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Cookie Preferences
                </h3>
                <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-muted rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">Necessary Cookies</p>
                    <p className="text-xs text-muted-foreground">
                      Required for the website to function properly. Cannot be disabled.
                    </p>
                  </div>
                  <div className="text-xs text-green-600 font-medium">Always On</div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">Analytics Cookies</p>
                    <p className="text-xs text-muted-foreground">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(p => ({...p, analytics: e.target.checked}))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                  </label>
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">Marketing Cookies</p>
                    <p className="text-xs text-muted-foreground">
                      Used to track visitors across websites for advertising purposes.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(p => ({...p, marketing: e.target.checked}))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={handleAcceptNecessary}>
                  Necessary Only
                </Button>
                <Button size="sm" onClick={handleSavePreferences} className="btn-accent">
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
